import { StrictMode, useEffect, useState } from 'react'
import type { ComponentType } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { matchRoute, loadRouteComponent } from './routes'
import { initUtmForwarding } from './lib/utm-forwarding'

const root = document.getElementById('root')!

// Resolve the per-route page chunk and any code-split content (the MDX body)
// before hydrating, so the client renders the same tree the server prerendered
// and hydration matches. Each prerendered HTML targets exactly one route, so
// we only ever load that route's chunk.
async function loadPage(pathname: string): Promise<ComponentType> {
  const route = matchRoute(pathname)
  const [Page] = await Promise.all([loadRouteComponent(route), route.preload?.()])
  return Page
}

function routeExists(pathname: string): boolean {
  const route = matchRoute(pathname)
  return route.path !== '/' || pathname.replace(/\/+$/, '') === ''
}

// The entry mounts the app and exports nothing, so fast refresh has nothing to keep here.
// eslint-disable-next-line react-refresh/only-export-components
function ClientApp({ initialPage }: { initialPage: ComponentType }) {
  const [Page, setPage] = useState(() => initialPage)
  const [url, setUrl] = useState(window.location.pathname)

  useEffect(() => {
    let cancelled = false
    // The path of the page on screen: a popstate that stays on it is not a page change.
    let shown = window.location.pathname

    const navigate = async (pathname: string, push: boolean) => {
      const nextPage = await loadPage(pathname)
      if (cancelled) return
      if (push) window.history.pushState({}, '', pathname)
      shown = pathname
      setPage(() => nextPage)
      setUrl(pathname)
      window.scrollTo({ top: 0, behavior: 'instant' })
    }

    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return
      }
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor || anchor.target || anchor.hasAttribute('download')) return
      const next = new URL(anchor.href, window.location.href)
      if (next.origin !== window.location.origin || next.hash || !routeExists(next.pathname)) return
      event.preventDefault()
      if (next.pathname !== window.location.pathname) void navigate(next.pathname, true)
    }

    // A fragment link (#method), a same-path deep link or back/forward between two
    // fragments of one page also fires popstate. The page is already shown and the
    // browser scrolls to the fragment itself, so only a path change reloads and
    // scrolls to the top.
    const onPopState = () => {
      if (window.location.pathname === shown) return
      void navigate(window.location.pathname, false)
    }

    document.addEventListener('click', onClick)
    window.addEventListener('popstate', onPopState)
    return () => {
      cancelled = true
      document.removeEventListener('click', onClick)
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  return <App Page={Page} url={url} />
}

async function mount() {
  const Page = await loadPage(window.location.pathname)
  const app = (
    <StrictMode>
      <ClientApp initialPage={Page} />
    </StrictMode>
  )
  // Prerendered HTML (production) has server markup to hydrate; the dev server
  // ships an empty shell, so render fresh there. The shell still holds the
  // `<!--app-html-->` placeholder comment, so test for an element, not any
  // node: hydrating against the comment logged a hydration mismatch in dev.
  if (root.firstElementChild) {
    hydrateRoot(root, app)
  } else {
    createRoot(root).render(app)
  }
}

// UTM forwarding runs for every visitor, before and independent of the
// consent-gated analytics below: it only rewrites app-link targets so campaign
// attribution survives the hop to app.langwatch.ai (see src/lib/utm-forwarding.ts).
initUtmForwarding()

// Analytics loads as its own chunk after hydration so it never competes with
// rendering the page (and never runs during the SSR prerender). Consent gates
// the import, so a visitor who declined or has not chosen yet never boots it;
// the cookie banner imports it on accept, and initAnalytics re-checks consent
// itself. Note posthog-js can still be pulled into the graph by VariantNav's
// static import, but nothing initialises it: no cookies, no network calls.
void mount().then(async () => {
  const { analyticsAllowed, readConsent } = await import('./lib/consent')
  if (!analyticsAllowed(readConsent())) return
  const { initAnalytics } = await import('./lib/analytics')
  initAnalytics()
})
